
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Clipboard, ClipboardCheck, FileText, Download, Play, Music, Video, AlertCircle, Volume2, Activity, Clock, MousePointer2, Settings2, Volume1, Edit3, Save, X, RotateCcw, CloudUpload, Loader2, Check, Pencil, UserCircle2, Languages } from 'lucide-react';
import { auth } from '../services/supabase';
import { uploadTranscriptMedia } from '../services/historyService';
import CommentPopover from './CommentPopover';
import VersionHistoryPanel from './VersionHistoryPanel';
import { translateText, SUPPORTED_LANGUAGES } from '../services/translationService';

interface TranscriptSegment {
  startTime: number;
  text: string;
  originalTimestamp: string;
  speakerId?: number; // -1 or undefined = no diarization
}

// ── Speaker color palette ────────────────────────────────────────────────────
const SPEAKER_COLORS: Array<{ bg: string; text: string; border: string }> = [
  { bg: 'bg-indigo-100 dark:bg-indigo-900/40',   text: 'text-indigo-700 dark:text-indigo-300',   border: 'border-indigo-300 dark:border-indigo-600' },
  { bg: 'bg-rose-100 dark:bg-rose-900/40',       text: 'text-rose-700 dark:text-rose-300',       border: 'border-rose-300 dark:border-rose-600'     },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-600'},
  { bg: 'bg-amber-100 dark:bg-amber-900/40',     text: 'text-amber-700 dark:text-amber-300',     border: 'border-amber-300 dark:border-amber-600'   },
  { bg: 'bg-sky-100 dark:bg-sky-900/40',         text: 'text-sky-700 dark:text-sky-300',         border: 'border-sky-300 dark:border-sky-600'       },
];

function getSpeakerColor(speakerId: number) {
  return SPEAKER_COLORS[speakerId % SPEAKER_COLORS.length];
}

// ── SpeakerChip ──────────────────────────────────────────────────────────────
interface SpeakerChipProps {
  speakerId: number;
  speakerLabels: Record<number, string>;
  onRenameLabel: (speakerId: number, newLabel: string) => void;
}

const SpeakerChip: React.FC<SpeakerChipProps> = ({ speakerId, speakerLabels, onRenameLabel }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const inputRef              = useRef<HTMLInputElement>(null);
  const color                 = getSpeakerColor(speakerId);
  const label                 = speakerLabels[speakerId] ?? `Speaker ${speakerId + 1}`;

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(label);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (trimmed) onRenameLabel(speakerId, trimmed);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditing(false);
    e.stopPropagation();
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        onClick={e => e.stopPropagation()}
        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-bold outline-none w-28 ${color.bg} ${color.text} ${color.border}`}
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      title="Click to rename speaker (updates all instances)"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold transition-all hover:opacity-80 shrink-0 ${color.bg} ${color.text} ${color.border}`}
    >
      <UserCircle2 size={11} />
      {label}
      <Pencil size={9} className="opacity-50" />
    </button>
  );
};
// ────────────────────────────────────────────────────────────────────────────

interface TranscriptPageProps {
  transcriptData: {
    id?: string;
    fileName: string;
    transcript?: string;
    originalTranscript?: string;
    file?: File;
    mediaData?: string;
    mediaType?: string;
    storageUrl?: string;
  };
  onBack: () => void;
  onSave?: (newTranscript: string) => void;
  isPaid?: boolean;
}

const WaveformVisualizer: React.FC<{ mediaElement: HTMLMediaElement | null; isPlaying: boolean }> = ({ mediaElement, isPlaying }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!mediaElement || !canvasRef.current) return;

    const setupAudio = () => {
      try {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        
        const ctx = audioContextRef.current;
        
        // Safety check: ensure we don't create multiple sources for the same element
        if (!sourceRef.current) {
            analyserRef.current = ctx.createAnalyser();
            analyserRef.current.fftSize = 256;
            
            // This line throws security error if CORS is not set on remote files
            try {
                sourceRef.current = ctx.createMediaElementSource(mediaElement);
                sourceRef.current.connect(analyserRef.current);
                analyserRef.current.connect(ctx.destination);
            } catch (e) {
                console.warn("Visualizer disabled due to CORS restrictions on remote media.");
                return;
            }
        }
      } catch(e) {
          console.error("Audio Context setup failed", e);
      }
    };

    const draw = () => {
      if (!analyserRef.current || !canvasRef.current) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * canvas.height;
        ctx.fillStyle = `rgba(99, 102, 241, ${Math.max(0.2, barHeight / canvas.height)})`;
        const radius = 4;
        const y = (canvas.height - barHeight) / 2;
        ctx.beginPath();
        if (ctx.roundRect) {
            ctx.roundRect(x, y, barWidth - 2, barHeight, radius);
        } else {
            ctx.rect(x, y, barWidth - 2, barHeight);
        }
        ctx.fill();
        x += barWidth + 1;
      }
      requestRef.current = requestAnimationFrame(draw);
    };

    const handlePlay = () => {
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      setupAudio();
      if (sourceRef.current) {
          draw();
      }
    };

    mediaElement.addEventListener('play', handlePlay);
    if (isPlaying) {
        handlePlay();
    }

    return () => {
      mediaElement.removeEventListener('play', handlePlay);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [mediaElement, isPlaying]);

  return (
    <div className="w-full h-16 relative flex items-center justify-center bg-brand-primary/5 rounded-xl border border-brand-primary/10 overflow-hidden">
        <canvas ref={canvasRef} width={800} height={64} className="w-full h-full opacity-80" />
        {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-base-100/10 backdrop-blur-[1px]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary/40 flex items-center gap-2">
                    <Activity size={12} />
                    Waiting for playback
                </p>
            </div>
        )}
    </div>
  );
};

const TranscriptPage: React.FC<TranscriptPageProps> = ({ transcriptData, onBack, onSave, isPaid = false }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFollowEnabled, setIsFollowEnabled] = useState(true);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState(transcriptData.transcript || '');
  const [editedTranscript, setEditedTranscript] = useState(transcriptData.transcript || '');
  const [mediaSourceType, setMediaSourceType] = useState<string>('unknown');
  
  // Cloud Sync state
  const [isRemoteMedia, setIsRemoteMedia] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [user, setUser] = useState(auth.currentUser);

  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement | null>(null);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // Monitor auth state changes
  useEffect(() => {
      const unsubscribe = auth.onAuthStateChanged((u) => {
          setUser(u);
      });
      return () => unsubscribe();
  }, []);

  // Update local state if prop changes
  useEffect(() => {
    setCurrentTranscript(transcriptData.transcript || '');
    setEditedTranscript(transcriptData.transcript || '');
  }, [transcriptData.transcript]);

  // Translation state
  const [translating, setTranslating]               = useState(false);
  const [modelProgress, setModelProgress]           = useState<number | null>(null);
  const [translateProgress, setTranslateProgress]   = useState<number | null>(null);
  const [translationOutput, setTranslationOutput]   = useState<string | null>(null);
  const [translationError, setTranslationError]     = useState<string | null>(null);
  const [sourceLang, setSourceLang]                 = useState('auto');
  const [targetLang, setTargetLang]                 = useState('en');
  const [translationCopied, setTranslationCopied]   = useState(false);

  // speakerLabels: map speakerId → human-readable label (editable, updates all instances)
  const [speakerLabels, setSpeakerLabels] = useState<Record<number, string>>({});

  const renameLabel = useCallback((speakerId: number, newLabel: string) => {
    setSpeakerLabels(prev => ({ ...prev, [speakerId]: newLabel }));
  }, []);

  // Parse the timestamped transcript (also extracts [SPEAKER:N] diarization tags)
  const segments = useMemo(() => {
    if (!currentTranscript) return [];

    const lines = currentTranscript.split('\n');
    const parsedSegments: TranscriptSegment[] = [];

    const timeRegex    = /\[(\d{1,2}:)?(\d{1,2}):(\d{2})\]/;
    const speakerRegex = /\[SPEAKER:(\d+)\]/;

    lines.forEach(line => {
      const match = line.match(timeRegex);
      if (match) {
        const timestamp = match[0];
        let remaining   = line.replace(timestamp, '').trim();

        // Extract optional speaker tag e.g. [SPEAKER:0]
        let speakerId: number | undefined;
        const speakerMatch = remaining.match(speakerRegex);
        if (speakerMatch) {
          speakerId = parseInt(speakerMatch[1], 10);
          remaining = remaining.replace(speakerMatch[0], '').trim();
        }

        const parts = timestamp.replace('[', '').replace(']', '').split(':').map(Number);
        let seconds = 0;
        if (parts.length === 3) {
          seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
          seconds = parts[0] * 60 + parts[1];
        }

        parsedSegments.push({
          startTime: seconds,
          text: remaining,
          originalTimestamp: timestamp,
          speakerId,
        });
      }
    });

    return parsedSegments;
  }, [currentTranscript]);

  useEffect(() => {
    let url: string | null = null;

    const setupMedia = async () => {
        // Priority 1: Direct File Object (Just uploaded or restored from session)
        if (transcriptData.file && transcriptData.file.size > 0) {
            url = URL.createObjectURL(transcriptData.file);
            setMediaUrl(url);
            setMediaSourceType(transcriptData.file.type);
            setIsRemoteMedia(false); // It's local
        } 
        // Priority 2: Cloud Storage URL (Fetched from Cloud)
        else if (transcriptData.storageUrl) {
            setMediaUrl(transcriptData.storageUrl);
            setMediaSourceType(transcriptData.mediaType || 'audio/mp3');
            setIsRemoteMedia(true);
        }
        // Priority 3: Base64 Data from DB (full data URL stored by FFmpeg service)
        else if (transcriptData.mediaData) {
            try {
                const dataUrl = transcriptData.mediaData.startsWith('data:')
                    ? transcriptData.mediaData
                    : `data:${transcriptData.mediaType || 'audio/mpeg'};base64,${transcriptData.mediaData}`;
                url = dataUrl;
                setMediaUrl(dataUrl);
                setMediaSourceType(transcriptData.mediaType || 'audio/mpeg');
                setIsRemoteMedia(false);
                console.log('[TranscriptPage] Loaded audio from DB media_data');
            } catch (e) {
                console.error('[TranscriptPage] Failed to load saved media', e);
            }
        }
    };

    setupMedia();

    return () => {
      if (url && (url.startsWith('blob:') || url.startsWith('data:'))) {
          URL.revokeObjectURL(url);
      }
    };
  }, [transcriptData]);

  useEffect(() => {
    const index = segments.findIndex((seg, idx) => {
      const nextSeg = segments[idx + 1];
      return currentTime >= seg.startTime && (!nextSeg || currentTime < nextSeg.startTime);
    });

    if (index !== activeSegmentIndex) {
      setActiveSegmentIndex(index);
      if (isFollowEnabled && index !== -1 && !isEditing) {
        segmentRefs.current[index]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [currentTime, segments, isFollowEnabled, activeSegmentIndex, isEditing]);

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };

  const handleSegmentClick = (startTime: number) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = startTime;
      mediaRef.current.play().catch(() => {});
    }
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(currentTranscript);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }, [currentTranscript]);

  const handleDownload = () => {
      const element = document.createElement("a");
      const file = new Blob([currentTranscript], {type: 'text/plain'});
      element.href = URL.createObjectURL(file);
      element.download = `${transcriptData.fileName}.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
  };

  const handleStartEdit = () => {
    setEditedTranscript(currentTranscript);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    setCurrentTranscript(editedTranscript);
    setIsEditing(false);
    if (onSave) {
        onSave(editedTranscript);
    }
  };

  const handleVersionRestore = (restoredTranscript: string) => {
    setCurrentTranscript(restoredTranscript);
    setEditedTranscript(restoredTranscript);
    if (onSave) onSave(restoredTranscript);
  };

  const handleTranslate = useCallback(async () => {
    if (!currentTranscript || translating) return;
    setTranslating(true);
    setTranslationOutput(null);
    setTranslationError(null);
    setModelProgress(null);
    setTranslateProgress(null);

    try {
      const result = await translateText(
        currentTranscript,
        sourceLang,
        targetLang,
        (pct) => setModelProgress(pct),
        (pct) => setTranslateProgress(pct),
      );
      setTranslationOutput(result);
    } catch (err) {
      setTranslationError(err instanceof Error ? err.message : String(err));
    } finally {
      setTranslating(false);
      setModelProgress(null);
      setTranslateProgress(null);
    }
  }, [currentTranscript, sourceLang, targetLang, translating]);

  const handleCopyTranslation = useCallback(() => {
    if (!translationOutput) return;
    navigator.clipboard.writeText(translationOutput);
    setTranslationCopied(true);
    setTimeout(() => setTranslationCopied(false), 2000);
  }, [translationOutput]);
  
  const handleCloudUpload = async () => {
      if (!transcriptData.file || !transcriptData.id) return;
      setIsUploading(true);
      try {
          await uploadTranscriptMedia(transcriptData.id, transcriptData.file);
          setUploadSuccess(true);
          setIsRemoteMedia(true); // Treat as remote now that it's uploaded (optional, but keeps visualizer active until reload)
          setTimeout(() => setUploadSuccess(false), 3000);
      } catch (e) {
          console.error("Upload failed", e);
          alert("Failed to upload media to cloud.");
      } finally {
          setIsUploading(false);
      }
  };

  const renderMediaPlayer = () => {
    if (!mediaUrl) {
        return (
            <div className="w-full bg-amber-500/5 dark:bg-amber-500/10 rounded-2xl p-6 mb-8 border border-amber-500/20 flex flex-col md:flex-row items-center gap-4 animate-fade-in">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-600 dark:text-amber-400">
                    <AlertCircle size={24} />
                </div>
                <div className="flex-grow text-center md:text-left">
                    <h4 className="font-bold text-base-content dark:text-dark-content">Playback Unavailable</h4>
                    <p className="text-sm text-base-content/60 dark:text-dark-content/60">
                        The media file is not available locally or in the cloud.
                    </p>
                </div>
            </div>
        );
    }

    const isAudio = mediaSourceType.startsWith('audio/');
    const isVideo = mediaSourceType.startsWith('video/');
    
    // Disable visualizer for remote files unless we uploaded it (local file obj still exists)
    // Note: The variable isRemoteMedia determines if we pulled it from Cloud URL
    const showVisualizer = !isRemoteMedia || (transcriptData.file !== undefined);
    const crossOrigin = (isRemoteMedia && !transcriptData.file) ? undefined : "anonymous";

    return (
        <div className="w-full bg-white dark:bg-dark-200 rounded-3xl overflow-hidden mb-8 border border-base-200 dark:border-dark-300 shadow-xl shadow-brand-primary/5 transition-all duration-300">
            {isAudio || !isVideo ? (
                // Default to Audio view
                <div className="p-8 flex flex-col md:flex-row items-center gap-8 bg-gradient-to-br from-brand-primary/5 to-transparent">
                    <div className="w-24 h-24 bg-brand-primary rounded-2xl flex items-center justify-center text-white shadow-2xl shadow-brand-primary/20 rotate-3 transition-transform hover:rotate-0 duration-500">
                        <Music size={40} />
                    </div>
                    <div className="flex-grow w-full space-y-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-xl font-bold text-base-content dark:text-dark-content flex items-center gap-2">
                                    Audio Engine
                                    {showVisualizer && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-primary text-white font-bold uppercase tracking-tighter">Live Analysis</span>
                                    )}
                                </h3>
                                <p className="text-sm text-base-content/50 dark:text-dark-content/50 truncate max-w-md">{transcriptData.fileName}</p>
                            </div>
                        </div>
                        
                        {showVisualizer ? (
                            <WaveformVisualizer mediaElement={mediaRef.current} isPlaying={isPlaying} />
                        ) : (
                             <div className="w-full h-16 relative flex items-center justify-center bg-base-200 dark:bg-dark-300 rounded-xl border border-base-300 dark:border-dark-100 overflow-hidden">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-base-content/40 dark:text-dark-content/40 flex items-center gap-2">
                                    <Activity size={12} />
                                    Visualizer Unavailable (Remote File)
                                </p>
                            </div>
                        )}

                        <audio 
                            ref={mediaRef as any}
                            src={mediaUrl} 
                            controls 
                            crossOrigin={crossOrigin}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onTimeUpdate={handleTimeUpdate}
                            className="w-full h-10 rounded-full focus:outline-none" 
                        />
                    </div>
                </div>
            ) : (
                <div className="relative group bg-black">
                    {showVisualizer && (
                        <div className="absolute top-4 left-4 z-20 w-1/3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <WaveformVisualizer mediaElement={mediaRef.current} isPlaying={isPlaying} />
                        </div>
                    )}
                    <video 
                        ref={mediaRef as any}
                        src={mediaUrl} 
                        controls 
                        crossOrigin={crossOrigin}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onTimeUpdate={handleTimeUpdate}
                        className="w-full max-h-[600px] object-contain mx-auto" 
                    />
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                         <div className="flex items-center gap-2 bg-black/50 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10 text-white text-xs">
                            <Video size={14} />
                            HD Preview
                         </div>
                    </div>
                </div>
            )}
        </div>
    );
  };

  const showCloudSave = user && !isRemoteMedia && !uploadSuccess && transcriptData.file;

  return (
    <div className="w-full max-w-5xl animate-fade-in px-4 pb-12">
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4 overflow-hidden">
            <button onClick={onBack} className="group p-3 rounded-2xl hover:bg-base-200 dark:hover:bg-dark-300 transition-all border border-transparent hover:border-base-300 dark:hover:border-dark-200 shrink-0" aria-label="Go back">
                <ArrowLeft size={24} className="text-base-content/70 dark:text-dark-content/70 group-hover:text-base-content dark:group-hover:text-dark-content" />
            </button>
            <div className="overflow-hidden">
                <h1 className="text-2xl font-bold text-base-content dark:text-dark-content truncate" title={transcriptData.fileName}>
                    {transcriptData.fileName}
                </h1>
                <div className="flex items-center gap-2 text-sm text-base-content/40 dark:text-dark-content/40">
                    <span>Transcription Complete</span>
                    <span className="w-1 h-1 bg-base-300 rounded-full"></span>
                    <span>{new Date().toLocaleDateString()}</span>
                </div>
            </div>
        </div>
        
        <div className="hidden sm:flex items-center gap-3 flex-wrap justify-end">
             {transcriptData.id && (
               <VersionHistoryPanel
                 transcriptId={transcriptData.id}
                 currentTranscript={currentTranscript}
                 isPaid={isPaid}
                 onRestore={handleVersionRestore}
               />
             )}
             {transcriptData.id && (
               <div className="relative">
                 <CommentPopover
                   transcriptId={transcriptData.id}
                   isPaid={isPaid}
                   containerRef={transcriptContainerRef}
                 />
               </div>
             )}
             {showCloudSave && (
                <button 
                  onClick={handleCloudUpload}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-primary/10 text-brand-primary border border-brand-primary/20 text-sm font-semibold hover:bg-brand-primary/20 transition-all"
                  title="Save media file to Cloud Storage"
                >
                    {isUploading ? <Loader2 size={18} className="animate-spin" /> : <CloudUpload size={18} />}
                    <span>{isUploading ? 'Uploading...' : 'Save to Cloud'}</span>
                </button>
            )}
            
            {uploadSuccess && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/10 text-green-600 border border-green-500/20 text-sm font-semibold">
                    <Check size={18} />
                    <span>Saved</span>
                </div>
            )}

            <button 
              onClick={() => setIsFollowEnabled(!isFollowEnabled)}
              disabled={isEditing}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all shadow-sm ${isFollowEnabled ? 'bg-brand-primary/10 text-brand-primary border-brand-primary/30' : 'bg-white dark:bg-dark-200 border-base-300 dark:border-dark-300 text-base-content/70'} disabled:opacity-40`}
              title="Toggle Auto-follow Scroll"
            >
                <MousePointer2 size={18} />
                <span>Follow Audio</span>
            </button>
            <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-dark-200 border border-base-300 dark:border-dark-300 text-sm font-semibold text-base-content/70 hover:text-brand-primary hover:border-brand-primary/50 transition-all shadow-sm">
                <Download size={18} />
                <span>Export TXT</span>
            </button>
        </div>
      </header>
      
      {renderMediaPlayer()}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
        <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/20 text-xs font-bold uppercase tracking-wider mb-2">
                <Volume2 size={12} />
                Result Data
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-3xl font-extrabold text-base-content dark:text-dark-content tracking-tight">
                  {isEditing ? 'Editor Mode' : 'Transcript'}
              </h2>
              {!isEditing && transcriptData.originalTranscript && (
                <div className="flex items-center gap-1 p-1 rounded-xl bg-base-200 dark:bg-dark-300 border border-base-300 dark:border-dark-400">
                  <button
                    onClick={() => setShowingOriginal(false)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${!showingOriginal ? 'bg-brand-primary text-white shadow-sm' : 'text-base-content/60 dark:text-dark-content/60 hover:text-base-content dark:hover:text-dark-content'}`}
                  >
                    Translated
                  </button>
                  <button
                    onClick={() => setShowingOriginal(true)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${showingOriginal ? 'bg-brand-primary text-white shadow-sm' : 'text-base-content/60 dark:text-dark-content/60 hover:text-base-content dark:hover:text-dark-content'}`}
                  >
                    Original
                  </button>
                </div>
              )}
            </div>
            <p className="text-base-content/50 dark:text-dark-content/50">
                {isEditing ? 'Editing the raw transcript data.' : 'Interactive transcript with synchronized highlighting.'}
            </p>
        </div>
        
        <div className="flex items-center gap-3">
            {!isEditing ? (
                <>
                <button onClick={handleStartEdit} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 text-sm font-bold text-base-content/70 hover:text-brand-primary hover:border-brand-primary/50 transition-all bg-white dark:bg-dark-200">
                    <Edit3 size={18} />
                    Edit Transcript
                </button>
                <button onClick={handleCopy} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl border text-sm font-bold transition-all shadow-lg active:scale-95 ${isCopied ? 'bg-green-500 text-white border-green-500 shadow-green-500/20' : 'bg-brand-primary text-white border-brand-primary shadow-brand-primary/20 hover:bg-brand-dark'}`}>
                    {isCopied ? <ClipboardCheck size={18} /> : <Clipboard size={18} />}
                    {isCopied ? 'Copied to Clipboard' : 'Copy Text'}
                </button>
                </>
            ) : (
                <>
                <button onClick={handleCancelEdit} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-red-500/20 text-sm font-bold text-red-500 bg-red-500/5 hover:bg-red-500/10 transition-all">
                    <X size={18} />
                    Cancel
                </button>
                <button onClick={handleSaveEdit} className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white text-sm font-bold transition-all shadow-lg shadow-green-600/20 hover:bg-green-700 active:scale-95">
                    <Save size={18} />
                    Save Changes
                </button>
                </>
            )}
        </div>
      </div>

      <div ref={transcriptContainerRef} className="group relative bg-white dark:bg-dark-200 rounded-[2rem] shadow-sm border border-base-200 dark:border-dark-300 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-brand-primary/5 min-h-[400px]">
         <div className="absolute top-0 left-0 w-2 h-full bg-brand-primary/10 group-hover:bg-brand-primary transition-colors duration-500" />
         
         <div className="p-4 md:p-8">
            {isEditing ? (
                <div className="w-full animate-fade-in">
                    <textarea 
                        value={editedTranscript}
                        onChange={(e) => setEditedTranscript(e.target.value)}
                        className="w-full h-[500px] p-6 rounded-2xl bg-base-100 dark:bg-dark-100 border border-base-300 dark:border-dark-300 focus:ring-4 focus:ring-brand-primary/10 focus:border-brand-primary outline-none text-lg md:text-xl font-sans leading-relaxed text-base-content dark:text-dark-content transition-all resize-none custom-scrollbar"
                        placeholder="Type your transcript here..."
                    />
                    <div className="mt-4 flex items-center gap-2 text-xs text-base-content/40 dark:text-dark-content/40">
                        <Clock size={14} />
                        <span>Tip: Maintain the [MM:SS] format at the start of lines to preserve synchronized playback features.</span>
                    </div>
                </div>
            ) : (
                <div className="max-h-[600px] overflow-y-auto custom-scrollbar scroll-smooth">
                    <div className="space-y-3">
                        {segments.length > 0 ? (
                            segments.map((segment, index) => (
                                <div 
                                key={index}
                                ref={el => { segmentRefs.current[index] = el; }}
                                onClick={() => handleSegmentClick(segment.startTime)}
                                className={`group flex items-start gap-6 p-6 rounded-3xl transition-all cursor-pointer duration-500 relative ${activeSegmentIndex === index ? 'bg-brand-primary/10 dark:bg-brand-primary/20 border border-brand-primary/20 shadow-[0_0_20px_rgba(99,102,241,0.1)] scale-[1.01]' : 'hover:bg-base-200/50 dark:hover:bg-dark-300/50 border border-transparent'}`}
                                >
                                    {activeSegmentIndex === index && (
                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-12 bg-brand-primary rounded-r-full animate-pulse" />
                                    )}
                                    
                                    <div className={`flex flex-col items-center gap-1 shrink-0 mt-1.5 transition-all duration-300 ${activeSegmentIndex === index ? 'text-brand-primary scale-110' : 'text-base-content/20 group-hover:text-base-content/40'}`}>
                                        <div className={`flex items-center gap-2 text-xs font-mono font-bold tracking-tight px-2 py-1 rounded-lg ${activeSegmentIndex === index ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/20' : 'bg-base-200 dark:bg-dark-300'}`}>
                                            {activeSegmentIndex === index ? <Volume1 size={14} className="animate-bounce" /> : <Clock size={12} />}
                                            {segment.originalTimestamp.replace('[', '').replace(']', '')}
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col gap-1 flex-grow min-w-0">
                                    {segment.speakerId !== undefined && segment.speakerId >= 0 && (
                                      <div onClick={e => e.stopPropagation()}>
                                        <SpeakerChip
                                          speakerId={segment.speakerId}
                                          speakerLabels={speakerLabels}
                                          onRenameLabel={renameLabel}
                                        />
                                      </div>
                                    )}
                                    <p className={`leading-relaxed text-lg md:text-xl font-sans transition-all duration-500 ${activeSegmentIndex === index ? 'text-base-content dark:text-dark-content font-semibold' : 'text-base-content/60 dark:text-dark-content/50'}`}>
                                        {segment.text.split(/(\s+)/).map((token, tokenIndex) => {
                                          if (/^\s+$/.test(token)) return token;
                                          const words = segment.text.split(/\s+/).filter(Boolean);
                                          const wordIndex = segment.text.split(/\s+/).filter(Boolean).indexOf(token);
                                          const nextSeg = segments[index + 1];
                                          const segDuration = nextSeg
                                            ? nextSeg.startTime - segment.startTime
                                            : Math.max(words.length * 0.5, 2);
                                          const wordTimestamp = segment.startTime + (wordIndex / Math.max(words.length, 1)) * segDuration;
                                          return (
                                            <span
                                              key={tokenIndex}
                                              data-time={wordTimestamp.toFixed(2)}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (mediaRef.current) {
                                                  mediaRef.current.currentTime = wordTimestamp;
                                                  mediaRef.current.play().catch(() => {});
                                                }
                                              }}
                                              className="cursor-pointer hover:text-brand-primary hover:underline decoration-brand-primary/40 underline-offset-2 transition-colors rounded px-0.5"
                                              title={`Seek to ${wordTimestamp.toFixed(1)}s`}
                                            >
                                              {token}
                                            </span>
                                          );
                                        })}
                                    </p>
                                    </div>
                                </div>
                            ))
                        ) : (showingOriginal ? transcriptData.originalTranscript : currentTranscript) ? (
                            <p className="whitespace-pre-wrap leading-relaxed text-base-content/80 dark:text-dark-content/80 font-sans text-lg md:text-xl p-8">
                                {showingOriginal ? transcriptData.originalTranscript : currentTranscript}
                            </p>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-64 text-base-content/20 dark:text-dark-content/20">
                                <FileText size={64} className="mb-4 opacity-30"/>
                                <p className="text-xl font-medium">Empty Transcript</p>
                                <p className="text-sm">No transcription content available.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
         </div>
      </div>
      

      {!isEditing && (
        <div className="mt-6 p-6 rounded-2xl bg-base-200/50 dark:bg-dark-200/50 border border-base-300 dark:border-dark-300">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <Languages size={18} className="text-brand-primary shrink-0" />
            <h3 className="text-base font-bold text-base-content dark:text-dark-content">Local Translation</h3>
          </div>
          <p className="text-xs text-base-content/50 dark:text-dark-content/50 mb-5">
            Runs entirely on-device. First use downloads the model (~50–300 MB).
          </p>

          {/* Language selectors + button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 mb-4">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label className="text-xs font-semibold text-base-content/60 dark:text-dark-content/60 uppercase tracking-wider">From</label>
              <select
                value={sourceLang}
                onChange={(e) => { setSourceLang(e.target.value); setTranslationOutput(null); setTranslationError(null); }}
                disabled={translating}
                className="w-full px-3 py-2 rounded-xl border border-base-300 dark:border-dark-300 bg-white dark:bg-dark-200 text-sm text-base-content dark:text-dark-content focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50"
              >
                <option value="auto">Auto-detect</option>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label} ({lang.nativeName})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <label className="text-xs font-semibold text-base-content/60 dark:text-dark-content/60 uppercase tracking-wider">To</label>
              <select
                value={targetLang}
                onChange={(e) => { setTargetLang(e.target.value); setTranslationOutput(null); setTranslationError(null); }}
                disabled={translating}
                className="w-full px-3 py-2 rounded-xl border border-base-300 dark:border-dark-300 bg-white dark:bg-dark-200 text-sm text-base-content dark:text-dark-content focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50"
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code} disabled={sourceLang === lang.code}>
                    {lang.label} ({lang.nativeName})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleTranslate}
              disabled={translating || sourceLang === targetLang || !currentTranscript}
              className="flex items-center gap-2 px-6 py-2 rounded-xl bg-brand-primary text-white text-sm font-bold shadow-lg shadow-brand-primary/20 hover:bg-brand-dark transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 self-end"
            >
              {translating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
              Translate
            </button>
          </div>

          {/* Progress: model download */}
          {translating && modelProgress !== null && translateProgress === null && (
            <div className="mb-4 space-y-1.5">
              <p className="text-xs font-semibold text-base-content/60 dark:text-dark-content/60">
                Downloading model... {modelProgress}%
              </p>
              <div className="w-full h-2 rounded-full bg-base-300 dark:bg-dark-300 overflow-hidden">
                <div
                  className="h-full rounded-full bg-brand-primary transition-all duration-300"
                  style={{ width: `${modelProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Progress: translating */}
          {translating && translateProgress !== null && (
            <div className="mb-4 space-y-1.5">
              <p className="text-xs font-semibold text-base-content/60 dark:text-dark-content/60">
                Translating... {translateProgress}%
              </p>
              <div className="w-full h-2 rounded-full bg-base-300 dark:bg-dark-300 overflow-hidden">
                <div
                  className="h-full rounded-full bg-brand-primary transition-all duration-300"
                  style={{ width: `${translateProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {translationError && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{translationError}</p>
            </div>
          )}

          {/* Output */}
          {translationOutput && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-base-content/60 dark:text-dark-content/60 uppercase tracking-wider">Translation</span>
                <button
                  onClick={handleCopyTranslation}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${translationCopied ? 'bg-green-500 text-white border-green-500' : 'bg-white dark:bg-dark-200 border-base-300 dark:border-dark-300 text-base-content/70 hover:text-brand-primary hover:border-brand-primary/50'}`}
                >
                  {translationCopied ? <ClipboardCheck size={13} /> : <Clipboard size={13} />}
                  {translationCopied ? 'Copied!' : 'Copy Translation'}
                </button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-base-content dark:text-dark-content bg-white dark:bg-dark-200 rounded-2xl border border-base-200 dark:border-dark-300 p-5 max-h-[400px] overflow-y-auto custom-scrollbar">
                {translationOutput}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptPage;
